import { Component, computed, inject, input, output } from '@angular/core';

import { BusinessSettingsStore } from '../../core/settings';

@Component({
  selector: 'app-stock-price-range-filter',
  standalone: true,
  imports: [],
  template: `
    <div class="stock-price-range-filter">
      <span class="stock-price-range-filter__label">{{ minLabel() }}</span>
      <span class="stock-price-range-filter__track">
        <input
          type="range"
          class="stock-price-range-filter__thumb stock-price-range-filter__thumb--min"
          aria-label="Precio mínimo"
          [min]="bounds().min"
          [max]="bounds().max"
          [value]="value()?.min ?? bounds().min"
          (input)="onMinChange($event)"
        />
        <input
          type="range"
          class="stock-price-range-filter__thumb stock-price-range-filter__thumb--max"
          aria-label="Precio máximo"
          [min]="bounds().min"
          [max]="bounds().max"
          [value]="value()?.max ?? bounds().max"
          (input)="onMaxChange($event)"
        />
      </span>
      <span class="stock-price-range-filter__label">{{ maxLabel() }}</span>
    </div>
  `,
  styles: [
    `
      .stock-price-range-filter {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        color: #5d4841;
        font-size: 0.85rem;
        font-weight: 600;
        white-space: nowrap;
      }

      /* One shared bar behind both knobs; the native range tracks are hidden. */
      .stock-price-range-filter__track {
        position: relative;
        width: 160px;
        height: 18px;
      }

      .stock-price-range-filter__track::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        height: 6px;
        transform: translateY(-50%);
        border-radius: 999px;
        background: #e7ddd7;
      }

      .stock-price-range-filter__thumb {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        margin: 0;
        appearance: none;
        -webkit-appearance: none;
        background: transparent;
        pointer-events: none;
      }

      .stock-price-range-filter__thumb::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        pointer-events: all;
        width: 14px;
        height: 14px;
        border: 2px solid #ffffff;
        border-radius: 50%;
        background: var(--accent);
        box-shadow: 0 1px 3px rgba(17, 24, 39, 0.35);
        cursor: grab;
      }

      .stock-price-range-filter__thumb::-moz-range-thumb {
        pointer-events: all;
        width: 14px;
        height: 14px;
        border: 2px solid #ffffff;
        border-radius: 50%;
        background: var(--accent);
        box-shadow: 0 1px 3px rgba(17, 24, 39, 0.35);
        cursor: grab;
      }

      .stock-price-range-filter__thumb--min {
        z-index: 2;
      }

      .stock-price-range-filter__thumb--max {
        z-index: 3;
      }
    `
  ]
})
export class StockPriceRangeFilterComponent {
  private readonly businessSettingsStore = inject(BusinessSettingsStore);

  readonly bounds = input.required<{ min: number; max: number }>();
  readonly value = input<{ min: number; max: number } | null>(null);
  readonly valueChange = output<{ min: number; max: number }>();

  readonly minLabel = computed(() => this.formatPrice(this.value()?.min ?? this.bounds().min));
  readonly maxLabel = computed(() => this.formatPrice(this.value()?.max ?? this.bounds().max));

  onMinChange(event: Event): void {
    const min = Number((event.target as HTMLInputElement).value);
    const current = this.value() ?? this.bounds();
    this.valueChange.emit({ min, max: current.max });
  }

  onMaxChange(event: Event): void {
    const max = Number((event.target as HTMLInputElement).value);
    const current = this.value() ?? this.bounds();
    this.valueChange.emit({ min: current.min, max });
  }

  private formatPrice(price: number): string {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: this.businessSettingsStore.currencyCode(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  }
}
